import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import {
  getUserPreferences,
  updateUserPreferences,
  getStoredLanguage,
  storeLanguage,
} from '@/services/userPreferencesService';

type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<Language>(() => getStoredLanguage());
  // Mutex flag — never read during render, so a ref avoids needless re-renders.
  const loadingRef = useRef(false);

  // Load user preferences from Firestore when user logs in
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (currentUser && !loadingRef.current) {
        loadingRef.current = true;
        try {
          const preferences = await getUserPreferences(currentUser.uid);
          if (preferences && preferences.language !== language) {
            setLanguageState(preferences.language);
            await i18n.changeLanguage(preferences.language);
          }
        } catch (error) {
          console.error('Error loading user language preference:', error);
        } finally {
          loadingRef.current = false;
        }
      }
    };

    if (currentUser) {
      loadUserPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, i18n]);

  useEffect(() => {
    // Update i18n language
    i18n.changeLanguage(language);

    // Save to localStorage
    storeLanguage(language);

    // Save to Firestore if user is logged in
    if (currentUser && !loadingRef.current) {
      updateUserPreferences(currentUser.uid, { language }).catch((error) => {
        console.error('Error saving language preference to Firestore:', error);
      });
    }
  }, [language, currentUser, i18n]);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
  };

  const toggleLanguage = () => {
    setLanguageState(prevLanguage => prevLanguage === 'en' ? 'es' : 'en');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
